import {
  CreateReportResponse,
  ReportFormat,
  ReportStatus,
  ReportTemplateResponse,
  ReportType,
} from 'compliance-api';
import omit from 'lodash/omit';
import { MouseEvent, useCallback, useState } from 'react';
import { useCreateReportMutation } from '@/modules/common/queries/reports.ts';
import { usePollReportUntilReady } from '@/modules/common/queries/reports.ts';
import { useUpdateReportTemplateMutation } from '@/modules/admin-dashboard/queries/reports-templates.ts';
import { Button, DisclosureListItem } from 'ui';
import { FileIcon, PinFilledBlueIcon, PinIcon } from 'ui/icons';
import { openInNewTab } from 'ui/utils';

interface FixedReportItemProps {
  template: ReportTemplateResponse;
}

const FixedReportItem = ({ template }: FixedReportItemProps) => {
  const { name } = template;

  const handleOnSuccess = useCallback(async (data: CreateReportResponse) => {
    const { id, name, type, format, status, url } = data.report;

    switch (status) {
      case ReportStatus.Completed:
        if (url) {
          openInNewTab(url);
          // programmaticallyDownloading(url, name);
        }
        break;

      case ReportStatus.Generating:
        setCreatedReportMeta({ id, name, type, format });
        break;

      default:
        break;
    }
  }, []);

  const { mutateAsync, isLoading } = useCreateReportMutation({
    onSuccess: handleOnSuccess,
  });

  const [createdReportMeta, setCreatedReportMeta] = useState<{
    id: number;
    name: string;
    type: ReportType;
    format: ReportFormat;
  } | null>(null);

  const isPolling = !!createdReportMeta;
  usePollReportUntilReady(
    {
      id: createdReportMeta?.id,
      filter_by_name: createdReportMeta?.name,
      filter_by_format: createdReportMeta?.format,
      filter_by_type: createdReportMeta?.type,
      page: 1,
      page_size: 20,
    },
    {
      enabled: isPolling,
      onCompleted: report => {
        if (report.url) {
          openInNewTab(report.url);
          // programmaticallyDownloading(report.url, report.name);
        }
        setCreatedReportMeta(null);
      },
      onFailed: () => {
        setCreatedReportMeta(null);
      },
    },
  );

  const { mutateAsync: updateTemplate, isLoading: isTemplateUpdating } =
    useUpdateReportTemplateMutation();

  const handleGenerate = useCallback(async () => {
    await mutateAsync(omit({ ...template, name }, ['date_range']));
  }, [mutateAsync, name, template]);

  const handleToggleFavoriteTemplate = useCallback(
    (id: number, is_favorite: boolean, e: MouseEvent) => {
      e.stopPropagation();
      updateTemplate({ id, is_favorite });
    },
    [updateTemplate],
  );

  return (
    <DisclosureListItem
      classes={{
        leftContent: 'max-w-[calc(100%_-_150px)]',
        leftMainString: 'w-full',
      }}
      leftMainString={
        <div className="flex gap-3 items-center truncate max-w-[100%]">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-200 shrink-0">
            <img alt="template" src={FileIcon} width={20} />
          </div>
          <div className="truncate max-w-[100%]" title={name}>
            {name}
          </div>
        </div>
      }
      rightContent={
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleGenerate}
            loading={isLoading || isPolling}
            disabled={isLoading || isPolling}
            className="min-w-[95px]"
          >
            Generate
          </Button>
          <button
            onClick={handleToggleFavoriteTemplate.bind(null, template.id, !template.is_favorite)}
            disabled={isTemplateUpdating}
            className="shrink-0"
          >
            <img alt="favorite" src={template.is_favorite ? PinFilledBlueIcon : PinIcon} />
          </button>
        </div>
      }
    />
  );
};

export default FixedReportItem;
